/**
 * CREATIVE DETONATOR - Phase 2: Arming
 * Trigger selection functionality
 *
 * Features:
 * - Trigger grid with category filtering
 * - Random selection (RANDOM 3)
 * - Clear selection
 * - Credit counter (Triggers x Formats)
 * - Idea preview showing expected output
 */

// v61
import { GeneratorState, elements, TriggerImageMap, loadImageConfig } from '../core/state.js';
import { goToPhase } from '../core/navigation.js';
import { addTrigger, removeTrigger, setTriggers } from '../../../js/store/actions/generator.js';
// Centralized pricing - Single Source of Truth (see frontend/js/core/pricing.js)
import { IMAGE_COSTS, TEXT_COSTS, getImageCost, calculateGenerationCost } from '../../../js/core/pricing.js';

// Access globals from main.js
const DataStore = window.DataStore;
const CategoryColors = window.CategoryColors;

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize Phase 2 (Arming)
 * Sets up trigger grid, category filters, and action buttons
 */
export async function initPhase2() {
  // Load image config first (needed for accurate credit calculation)
  await loadImageConfig();

  // Update brief display with multi-format support
  const { product, benefit, formats } = GeneratorState.brief;
  const formatDisplay = formats.map(f => f.toUpperCase()).join(' + ');
  elements.armingBriefText.textContent = `${product} → ${benefit} → [${formatDisplay}]`;

  // Render trigger grid
  renderTriggerSelectionGrid();

  // Category tabs
  elements.categoryTabs.forEach(tab => {
    tab.addEventListener('click', () => {
      elements.categoryTabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      filterTriggersByCategory(tab.dataset.category);
    });
  });

  // Random 3 button
  document.getElementById('select-random').addEventListener('click', selectRandomTriggers);

  // Clear button
  document.getElementById('clear-selection').addEventListener('click', clearSelection);

  // Ideas count selector
  initIdeasCountSelector();

  // Detonate button
  elements.btnDetonate.addEventListener('click', () => {
    if (GeneratorState.selectedTriggers.length > 0 && !GeneratorState.isGenerating) {
      goToPhase(3);
    }
  });

  // Edit brief button
  document.getElementById('edit-brief').addEventListener('click', () => {
    goToPhase(1);
  });

  // Update credit counter with loaded config
  updateCreditCounter();
}

// ============================================================================
// IDEAS COUNT SELECTOR
// ============================================================================

/**
 * Initialize the ideas count selector buttons
 */
function initIdeasCountSelector() {
  const buttons = document.querySelectorAll('.ideas-count-btn');

  // Set initial state from GeneratorState (default 3)
  const currentCount = GeneratorState.ideasCount || 3;
  buttons.forEach(btn => {
    const count = parseInt(btn.dataset.count, 10);
    if (count === currentCount) {
      btn.classList.add('active');
    } else {
      btn.classList.remove('active');
    }
  });

  // Add click handlers
  buttons.forEach(btn => {
    btn.addEventListener('click', () => {
      const count = parseInt(btn.dataset.count, 10);
      setIdeasCount(count);

      // Update active state
      buttons.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      // Update preview
      updateIdeaPreview();
    });
  });
}

/**
 * Set the ideas count in state
 * @param {number} count - Number of ideas per trigger
 */
function setIdeasCount(count) {
  GeneratorState.ideasCount = count;
}

// ============================================================================
// TRIGGER GRID
// ============================================================================

/**
 * Render the trigger selection grid
 * Creates clickable cards for all available triggers
 */
function renderTriggerSelectionGrid() {
  const triggers = DataStore.triggers;
  if (!triggers) return;

  elements.triggerGrid.innerHTML = triggers.map(trigger => {
    const color = CategoryColors[trigger.category];
    const imageFile = TriggerImageMap[trigger.code];
    const imagePath = imageFile ? `/assets/images/triggers/${imageFile}` : '';
    // Light backgrounds need dark text: C (Cyan), D (Lime), F (Yellow)
    const textColor = ['C', 'D', 'F'].includes(trigger.category) ? '#000' : '#fff';
    return `
      <div class="trigger-select-card" data-code="${trigger.code}" data-category="${trigger.category}" style="border-left: 4px solid ${color}">
        ${imagePath ? `<img class="trigger-select-image" src="${imagePath}" alt="${trigger.name}">` : ''}
        <div class="trigger-select-code" style="background: ${color}; color: ${textColor};">${trigger.code}</div>
        <div class="trigger-select-name">${trigger.name.toUpperCase()}</div>
      </div>
    `;
  }).join('');

  // Add click handlers
  elements.triggerGrid.querySelectorAll('.trigger-select-card').forEach(card => {
    card.addEventListener('click', () => {
      toggleTriggerSelection(card);
    });
  });
}

/**
 * Toggle trigger selection state
 * @param {HTMLElement} card - The trigger card element
 */
function toggleTriggerSelection(card) {
  const code = card.dataset.code;
  const isSelected = GeneratorState.selectedTriggers.includes(code);

  if (isSelected) {
    removeTrigger(code);
    card.classList.remove('selected');
  } else {
    addTrigger(code);
    card.classList.add('selected');
  }

  updateSelectionCount();
}

// ============================================================================
// SELECTION MANAGEMENT
// ============================================================================

/**
 * Update the selection count display
 */
function updateSelectionCount() {
  const count = GeneratorState.selectedTriggers.length;
  elements.selectionCount.textContent = count;

  // Update credit counter
  updateCreditCounter();
}

// Formats that can have auto-generated images
const VISUAL_FORMATS = ['print', 'social', 'digital', 'experiential'];

/**
 * Credit counter: Text credits + Image credits
 * Uses centralized pricing from frontend/js/core/pricing.js
 * - Text: TEXT_COSTS.standard credits per trigger × format
 * - Images: varies by provider, per idea for visual formats in autoFormats
 * Updates the selection count and detonate button state
 */
export function updateCreditCounter() {
  const triggerCount = GeneratorState.selectedTriggers.length;
  const formatCount = GeneratorState.brief.formats.length;
  const selectedFormats = GeneratorState.brief.formats;
  const ideasPerTrigger = GeneratorState.ideasCount || 3;
  const imageConfig = GeneratorState.imageConfig || {};

  // Use centralized pricing calculation
  const { textCredits, imageCredits, totalCredits } = calculateGenerationCost({
    triggerCount,
    formatCount,
    ideasPerTrigger,
    autoImageEnabled: imageConfig.autoGeneration || false,
    autoImageProvider: imageConfig.autoProvider || 'seedream',
    autoImageFormats: imageConfig.autoFormats || ['print'],
    selectedFormats,
    model: 'standard'
  });

  // Update selection count in button (show total credits)
  if (elements.selectionCount) {
    elements.selectionCount.textContent = totalCredits;
  }

  // Update detonate button state
  if (elements.btnDetonate) {
    elements.btnDetonate.disabled = !(triggerCount > 0 && formatCount > 0);
  }

  // Update idea preview text
  updateIdeaPreview();
}

/**
 * Idea Preview: Shows how many ideas will be generated per format
 * Formula: Ideas = Triggers x ideasCount (each trigger generates N ideas per format)
 */
function updateIdeaPreview() {
  const triggerCount = GeneratorState.selectedTriggers.length;
  const formats = GeneratorState.brief.formats;
  const ideasPerTrigger = GeneratorState.ideasCount || 3;

  if (!elements.ideaPreviewText) return;

  // Hide if nothing selected
  if (triggerCount === 0 || formats.length === 0) {
    elements.ideaPreviewText.textContent = '';
    return;
  }

  // Format display names
  const formatNames = {
    social: 'social media',
    video: 'video ad',
    print: 'print ad',
    experiential: 'experiential',
    digital: 'digital',
    campaign: 'full campaign'
  };

  // Build sentence parts for each format
  const parts = formats.map(format => {
    const count = triggerCount * ideasPerTrigger;
    const name = formatNames[format] || format;
    return `${count} ${name} idea${count !== 1 ? 's' : ''}`;
  });

  // Join with proper grammar
  let sentence = 'This will generate ';
  if (parts.length === 1) {
    sentence += parts[0] + '.';
  } else if (parts.length === 2) {
    sentence += parts[0] + ' and ' + parts[1] + '.';
  } else {
    sentence += parts.slice(0, -1).join(', ') + ', and ' + parts[parts.length - 1] + '.';
  }

  elements.ideaPreviewText.textContent = sentence;
}

// ============================================================================
// FILTERING
// ============================================================================

/**
 * Filter triggers by category
 * @param {string} category - Category code or 'all'
 */
function filterTriggersByCategory(category) {
  const cards = elements.triggerGrid.querySelectorAll('.trigger-select-card');

  cards.forEach(card => {
    if (category === 'all' || card.dataset.category === category) {
      card.classList.remove('hidden');
    } else {
      card.classList.add('hidden');
    }
  });
}

// ============================================================================
// QUICK ACTIONS
// ============================================================================

/**
 * Select 3 random triggers
 */
function selectRandomTriggers() {
  clearSelection();

  const triggers = DataStore.triggers;
  const shuffled = [...triggers].sort(() => 0.5 - Math.random());
  const selected = shuffled.slice(0, 3);

  selected.forEach(trigger => {
    const card = elements.triggerGrid.querySelector(`[data-code="${trigger.code}"]`);
    if (card) {
      addTrigger(trigger.code);
      card.classList.add('selected');
    }
  });

  updateSelectionCount();
}

/**
 * Clear all trigger selections
 */
function clearSelection() {
  setTriggers([]);
  elements.triggerGrid.querySelectorAll('.trigger-select-card').forEach(card => {
    card.classList.remove('selected');
  });
  updateSelectionCount();
}

// ============================================================================
// GLOBAL EXPORTS FOR COMPATIBILITY
// ============================================================================

// Expose to window for compatibility
window.initPhase2 = initPhase2;
window.updateCreditCounter = updateCreditCounter;
